import flat from 'flat';
import { Base64 } from 'js-base64';
import filterFunctions from 'js/constants/filterFunctions';
import filter from 'lodash/filter';
import find from 'lodash/find';
import findKey from 'lodash/findKey';
import forEach from 'lodash/forEach';
import has from 'lodash/has';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isInteger from 'lodash/isInteger';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import keys from 'lodash/keys';
import map from 'lodash/map';
import parseInt from 'lodash/parseInt';
import reduce from 'lodash/reduce';
import Constants from '../Constants';
import {
  DefaultsTypes,
  FilterTypes,
  FilterTypesKeys,
  TRANSFORM_RULES_PROTO_ATTR as PARAMS_PROTO_ATTR,
  TRANSFORM_RULES_PROTO_STR as PARAMS_PROTO_STR,
  RECIPE_GROUP_ANNOTATIONS,
  RECIPE_GROUP_DEFAULTS,
  RECIPE_GROUP_FILTERS,
  RECIPE_GROUP_TRANSFORMS,
  RECIPE_GROUP_TRANSFORMS_AGGREGATE,
  RECIPE_GROUP_TRANSFORMS_CODE,
  RECIPE_GROUP_TRANSFORMS_REPLICATE,
  RECIPE_GROUP_VALIDATIONS,
  TransformParameterTypes,
  TransformTypes,
  TX_BUILDER_RECIPE_GROUPS,
} from '../constants/schemaEditor';
import { GROUPED_TRANSFORM_RULES } from '../constants/transforms';
import FilterDecomposer from './FilterDecomposer';
import FilterDSLParser from './FilterDSLParser';
import JSONUtils from './JSONUtils';
import { completeTxBuilderRecipeGroups } from './RecipeBuilderUtils';
import { isDetectedDataset } from './ResourceUtils';
import SchemaAnnotater from './SchemaAnnotater';
import SchemaAnnotationUtils from './SchemaAnnotationUtils';
import TransformComposer from './TransformComposer';
import TransformDecomposer from './TransformDecomposer';
import TransformDSLParser from './TransformDSLParser';

var _ = require('lodash');

const ATTR_DEFAULT_NONE = {
  type: 'defaults',
  __destAttr: '',
  parameters: {
    defaultType: DefaultsTypes.DEFAULT_NONE,
    value: null,
  },
};

/**
 * NEXLA_SHADOW_ATTR - Internal shadow attribute used in JOLT scaffolding
 *
 * This attribute is automatically added to shift/default/remove operations
 * as part of the "scaffolding" that wraps transformation logic.
 *
 * Purpose:
 * - Ensures consistent JOLT structure even with empty transformations
 * - Provides a marker attribute that can be tracked through the transform pipeline
 * - Gets removed at the end (via the remove operation)
 *
 * Usage in scaffolding:
 * - shift:   { "#__#__NEX_ATTR__#__#": "#__#__NEX_ATTR__#__#" }
 * - default: { "#__#__NEX_ATTR__#__#": 1 }
 * - remove:  { "#__#__NEX_ATTR__#__#": "" }
 *
 * Multi-block significance:
 * When filtering scaffolding operations in code-first or subsequent builder blocks,
 * we identify scaffolding defaults by checking for this key in the spec.
 * User-defined defaults won't have this key, so they pass through the filter.
 */
const NEXLA_SHADOW_ATTR = '#__#__NEX_ATTR__#__#';
const getValForParam = (
  paramsInfo,
  paramName,
  currentTx,
  convertToLiteral = false,
  isArrayParam = false,
  isObjectParam = false,
) => {
  if (!has(paramsInfo, paramName)) {
    return null;
  }

  if (isArrayParam) {
    return map(paramsInfo[paramName], (item) =>
      getValForSingleParam(item, currentTx, convertToLiteral),
    );
  }

  if (isObjectParam) {
    const object = {};

    forEach(paramsInfo[paramName], (item, index) => {
      if (index % 2 === 0) {
        const key = item?.value;
        const value = getValForSingleParam(
          paramsInfo[paramName][index + 1],
          currentTx,
          convertToLiteral,
        );
        object[key] = value;
      }
    });

    return object;
  }

  return getValForSingleParam(paramsInfo[paramName], currentTx, convertToLiteral);
};

const getValForSingleParam = (param, currentTx, convertToLiteral = false) => {
  let { type, name, value, parameters } = param;
  let retVal;

  if (type === TransformParameterTypes.FUNCTION) {
    let newRule = {
      name,
      parameters,
    };
    retVal = applyTransformRule(currentTx, newRule);
  } else if (
    type === TransformParameterTypes.ATTR_REF ||
    type === TransformParameterTypes.SYS_ATTR_REF
  ) {
    retVal = has(param, 'name') ? name : null;
  } else if (convertToLiteral === true && type === 'string') {
    retVal = has(param, 'value') ? `$.${value}` : null;
  } else if (convertToLiteral === true && type === 'number') {
    retVal = has(param, 'value') ? `$_${value}` : null;
  } else {
    retVal = has(param, 'value') ? value : null;
  }

  return retVal;
};

function getTransformForRule(ruleValue, groupedTransformRules) {
  return _.has(groupedTransformRules.rules, ruleValue)
    ? groupedTransformRules.rules[ruleValue]
    : null;
}

function initTx(hasCustomTransform = false, enableAutoMapping = false) {
  let tx = TransformComposer.create();

  if (enableAutoMapping === true) {
    tx = setShareAllAttributes(true, tx);
  } else {
    // tx = TransformsUtils.setFlattenFirst(true, tx);
    tx = setShareAllAttributes(false, tx);

    if (hasCustomTransform === false) {
      tx = addNexlaShadowAttr(tx);
    }
  }

  return tx;
}

function setShareAllAttributes(shareAll = false, tx) {
  tx.setIncludeAll(shareAll);

  return tx;
}

function addNexlaShadowAttr(tx) {
  tx.setAttributeDefault(NEXLA_SHADOW_ATTR, 1);
  tx.includeAttribute(NEXLA_SHADOW_ATTR, NEXLA_SHADOW_ATTR);

  return tx;
}

function removeNexlaShadowAttr(tx) {
  tx.removeAttribute(NEXLA_SHADOW_ATTR);
  return tx;
}

function setFlattenFirst(flatten = true, tx) {
  // tx.setFlattenFirst(flatten);
  return tx;
}

function getTransformSpecForFlatToNested() {
  let tx = TransformComposer.create();
  tx.setNestedFirst(true);
  return tx.getTransformObject();
}

function setTransformFeatures(tx, features) {
  // tx.setTransformFeatures(features);
  return tx;
}
//Compose transform rules
function batchComposeTxForRules(tx, txRules) {
  if (txRules.customTx) {
    tx.setCustomTx(txRules.customTx);
    return tx;
  }

  let { items, orderByAttrId } = txRules;
  // console.log('batchComposeTxForRules');
  // console.log(JSON.stringify(txRules));
  forEach(orderByAttrId, (attrId, idx) => {
    let thisRule = items[attrId];
    tx = composeTxForRule(tx, thisRule.share, thisRule.modify, thisRule.filters, thisRule.defaults);
  });

  return tx;
}

const validateTxRule = (txRule) => {
  if (_.isNil(txRule.name) || _.isNil(txRule.parameters) || _.isEmpty(txRule.parameters)) {
    return false;
  }

  const { parameters, name } = txRule;
  //1. check if this txRule has all required Parameters are not.

  let requiredParamsForThisRule = _.has(GROUPED_TRANSFORM_RULES.rules, name)
    ? GROUPED_TRANSFORM_RULES.rules[name].requiredParams
    : [];
  let availableParams = _.keys(parameters);
  let missingParams = _.difference(requiredParamsForThisRule, availableParams);
  if (!_.isEmpty(missingParams)) {
    return false;
  }

  let ruleValid = true;

  _.forOwn(parameters, (ruleDesc, ruleName) => {
    //2. validate each parameter.
    //if any param was a function, get validateTxRule again. if any returned param results in failure break the iteration.

    if (_.isNil(ruleDesc) || !_.has(ruleDesc, 'type')) {
      return false;
    }

    let type = ruleDesc.type;
    // let paramRuleValid = true;

    switch (type) {
      case TransformParameterTypes.FUNCTION: {
        if (
          !_.has(ruleDesc, 'name') ||
          _.isNil(ruleDesc.name) ||
          ruleDesc.name === TransformTypes.TRANSFORM_NONE
        ) {
          ruleValid = false;
          return false;
        } else {
          ruleValid = validateTxRule(ruleDesc);
          return ruleValid;
        }
        // return ruleValid;
      }

      case TransformParameterTypes.ATTR_REF:
      case TransformParameterTypes.SYS_ATTR_REF:
        if (
          (!_.has(ruleDesc, 'name') || _.isNil(ruleDesc.name)) &&
          (!_.has(ruleDesc, '__ignoreSrc') || ruleDesc.__ignoreSrc !== true)
        ) {
          ruleValid = false;
          return false;
        }
        break;
      case TransformParameterTypes.STRING_INP:
      case TransformParameterTypes.NUMBER_INP:
        if (!_.has(ruleDesc, 'value') || _.isUndefined(ruleDesc.value)) {
          ruleValid = false;
          return false;
        }
        break;

      default:
    }

    return ruleValid;
  });
  return ruleValid;
};

const validateFilterRule = (filterRule) => {
  let { type, parameters } = filterRule;
  let filtersValid = true;

  if (type && type !== TransformParameterTypes.FILTER_GROUP) {
    filtersValid = false;
  } else if (
    parameters &&
    parameters.length > 1 &&
    _.some(parameters, (p) => {
      return _.has(p, 'ruleOp') && p.ruleOp === 'FILTER_NONE';
    })
  ) {
    filtersValid = false;
  }

  return filtersValid;
};

function composeTxForRule(tx, shareRules, modifyRules, filterRules, defaultsRules) {
  let isMetaData =
    modifyRules &&
    has(modifyRules, 'parameters.__srcAttr.type') &&
    modifyRules.parameters.__srcAttr.type === TransformParameterTypes.SYS_ATTR_REF
      ? true
      : false;

  let sourceAttr =
    modifyRules &&
    has(modifyRules, 'parameters.__srcAttr.name') &&
    (modifyRules.parameters.__srcAttr.type === TransformParameterTypes.SYS_ATTR_REF ||
      modifyRules.parameters.__srcAttr.type === TransformParameterTypes.ATTR_REF)
      ? modifyRules.parameters.__srcAttr.name
      : shareRules?.__destAttr;

  if (shareRules && shareRules.parameters.__shouldShare) {
    if (isMetaData) {
      tx.includeMetadataAttribute(sourceAttr, shareRules.__destAttr);
    } else {
      //  tx.includeAttribute(shareRules.__destAttr, shareRules.__destAttr);
    }
  } else if (shareRules && !shareRules.parameters.__shouldShare) {
    //    tx.removeAttribute(shareRules.__destAttr);
  }

  if (modifyRules) {
    tx = applyTransformRule(tx, modifyRules, true);
  }

  if (filterRules) {
    tx = applyFilterRules(tx, filterRules);
  }

  if (defaultsRules) {
    tx = applyDefaultsRules(tx, defaultsRules);
  }

  return tx;
}

const applyFilterRules = (tx, filterRules) => {
  //{"type":"filterGroup","parameters":[{"type":"filterRule","ruleAttribute":"listPrice","ruleOp":"FILTER_GT","ruleVal":{"type":"number","value":1}}]}

  // console.log('filter rules');
  // console.log(JSON.stringify(filterRules));
  if (
    isNull(filterRules) ||
    _.isEmpty(filterRules) ||
    !_.has(filterRules, 'type') ||
    filterRules.type !== TransformParameterTypes.FILTER_GROUP ||
    !_.has(filterRules, 'parameters')
  ) {
    //TODO: change this when filter view is moved from attribute to record persistance
    return tx;
  }
  let groupParameters = filterRules.parameters;
  if (
    _.isEmpty(groupParameters) ||
    !_.has(groupParameters[0], 'ruleOp') ||
    groupParameters[0].ruleOp === 'FILTER_NONE'
  ) {
    return tx;
  }
  // console.log('bypass');
  // return tx;
  tx.startFilterGroup();
  let groupParamCount = groupParameters.length;
  forEach(groupParameters, (rule, idx) => {
    if (rule.type === TransformParameterTypes.FILTER_RULE_JOIN_OP) {
      return true;
    }
    if (rule.type === TransformParameterTypes.FILTER_RULE) {
      let ruleAttribute = rule.ruleAttribute;
      let filterValue =
        rule.ruleVal.type === 'number' ? _.toNumber(rule.ruleVal.value) : rule.ruleVal.value;
      let filterOp = _.has(FilterTypes, rule.ruleOp) ? FilterTypes[rule.ruleOp].op : '';

      let ruleJoinOp =
        idx < groupParamCount - 1 &&
        groupParameters[idx + 1].type === TransformParameterTypes.FILTER_RULE_JOIN_OP
          ? groupParameters[idx + 1].value
          : null;
      let isCaseSensitive = false;
      switch (rule.ruleOp) {
        case FilterTypes.FILTER_REGEX_CONTAINS.value:
          isCaseSensitive = _.has(rule, 'ruleOptions.caseSensitive')
            ? rule.ruleOptions.caseSensitive
            : false;
          tx.containsFilter(ruleAttribute, filterValue, isCaseSensitive, ruleJoinOp);
          break;

        case FilterTypes.FILTER_REGEX_BEGINS.value:
          isCaseSensitive = _.has(rule, 'ruleOptions.caseSensitive')
            ? rule.ruleOptions.caseSensitive
            : false;
          tx.beginsWithFilter(ruleAttribute, filterValue, isCaseSensitive, ruleJoinOp);
          break;

        case FilterTypes.FILTER_REGEX_ENDS.value:
          isCaseSensitive = _.has(rule, 'ruleOptions.caseSensitive')
            ? rule.ruleOptions.caseSensitive
            : false;

          tx.endsWithFilter(ruleAttribute, filterValue, isCaseSensitive, ruleJoinOp);
          break;

        case FilterTypes.FILTER_IS_PRESENT.value:
        case FilterTypes.FILTER_IS_NOT_PRESENT.value:
          tx.addFilter(ruleAttribute, filterOp, null, ruleJoinOp);
          break;

        default:
          tx.addFilter(ruleAttribute, filterOp, filterValue, ruleJoinOp);
          // has(rule, 'parameters.__op')
          //   ? tx.addFilter(
          //       ruleAttribute,
          //       rule.parameters.name.op,
          //       rule.parameters.__value,
          //       rule.parameters.__op
          //     )
          //   : tx.addFilter(ruleAttribute, filterOp, filterValue);
          break;
      }
    }
  });
  tx.endFilterGroup();
  return tx;
};

const applyDefaultsRules = (tx, newDefaultsRule) => {
  const {
    parameters: { defaultType, value },
    __destAttr,
  } = newDefaultsRule;

  switch (defaultType) {
    case DefaultsTypes.DEFAULT_STR:
      !_.isNil(value)
        ? tx.setAttributeDefault(__destAttr, `${value}`)
        : tx.setAttributeDefault(__destAttr, ``);
      break;
    case DefaultsTypes.DEFAULT_NUM:
      tx.setAttributeDefault(__destAttr, _.toNumber(value));
      break;

    case DefaultsTypes.DEFAULT_NULL:
      tx.setAttributeDefault(__destAttr, null);
      break;
    default:
      tx.removeAttributeDefault(__destAttr);
  }

  return tx;
};

const applyTransformRule = (tx, newTransformRule, shouldCommitTx = false) => {
  const { name, parameters: ruleParams, __destAttr } = newTransformRule;
  const { __srcAttr } = ruleParams;
  let txModifier = tx;
  let __srcAttrName = null;
  if (__srcAttr.type === TransformParameterTypes.FUNCTION) {
    let newTxRule = {
      name: __srcAttr.name,
      parameters: __srcAttr.parameters,
    };
    __srcAttrName = applyTransformRule(tx, newTxRule);
  } else {
    __srcAttrName = ruleParams.__srcAttr.name;
  }

  switch (name) {
    //Security Transforms
    case 'md5':
    case 'sha256':
      txModifier = shouldCommitTx
        ? tx.map.hash(true, name, __srcAttrName).done(__destAttr)
        : tx.map.hash(true, name, __srcAttrName);
      break;

    //Math functions
    case 'abs':
      txModifier = shouldCommitTx
        ? tx.math.abs(__srcAttrName).done(__destAttr)
        : tx.math.abs(__srcAttrName);
      break;

    case 'add':
      {
        let add_with = getValForParam(ruleParams, 'add_with', tx, true);

        txModifier = shouldCommitTx
          ? tx.math.add(__srcAttrName, add_with).done(__destAttr)
          : tx.math.add(__srcAttrName, add_with);
      }
      break;

    case 'diff':
      {
        let to_subtract = getValForParam(ruleParams, 'to_subtract', tx, true);

        txModifier = shouldCommitTx
          ? tx.math.diff(__srcAttrName, to_subtract).done(__destAttr)
          : tx.math.diff(__srcAttrName, to_subtract);
      }
      break;

    case 'multiply':
      {
        let multiply_by = getValForParam(ruleParams, 'multiply_by', tx, true);

        txModifier = shouldCommitTx
          ? tx.math.multiply(__srcAttrName, multiply_by).done(__destAttr)
          : tx.math.multiply(__srcAttrName, multiply_by);
      }
      break;

    case 'div':
      {
        let div_by = getValForParam(ruleParams, 'div_by', tx, true);

        txModifier = shouldCommitTx
          ? tx.math.div(__srcAttrName, div_by).done(__destAttr)
          : tx.math.div(__srcAttrName, div_by);
      }
      break;

    case 'avg':
      txModifier = shouldCommitTx
        ? tx.math.avg(__srcAttrName).done(__destAttr)
        : tx.math.avg(__srcAttrName);
      break;

    case 'min':
      txModifier = shouldCommitTx
        ? tx.math.min(__srcAttrName).done(__destAttr)
        : tx.math.min(__srcAttrName);
      break;

    case 'max':
      txModifier = shouldCommitTx
        ? tx.math.max(__srcAttrName).done(__destAttr)
        : tx.math.max(__srcAttrName);
      break;

    case 'bucket':
      {
        let bucket_size = getValForParam(ruleParams, 'bucket_size');
        let bucketSize = bucket_size ? parseInt(bucket_size) : null;
        if (!isInteger(bucketSize)) {
          bucketSize = 0;
        }

        txModifier = shouldCommitTx
          ? tx.math.bucket(bucketSize, __srcAttrName).done(__destAttr)
          : tx.math.bucket(bucketSize, __srcAttrName);
      }
      break;

    case 'toDouble':
      txModifier = shouldCommitTx
        ? tx.math.toDouble(__srcAttrName).done(__destAttr)
        : tx.math.toDouble(__srcAttrName);
      break;

    case 'toInteger':
      txModifier = shouldCommitTx
        ? tx.math.toInteger(__srcAttrName).done(__destAttr)
        : tx.math.toInteger(__srcAttrName);
      break;

    case 'toLong':
      txModifier = shouldCommitTx
        ? tx.math.toLong(__srcAttrName).done(__destAttr)
        : tx.math.toLong(__srcAttrName);
      break;

    case 'precision':
      {
        let scale = getValForParam(ruleParams, 'scale');
        let intScale = scale ? parseInt(scale) : null;
        if (!isInteger(intScale)) {
          intScale = 0;
        }

        txModifier = shouldCommitTx
          ? tx.math.precision(intScale, __srcAttrName).done(__destAttr)
          : tx.math.precision(intScale, __srcAttrName);
      }
      break;

    //String operations:
    case 'concatArr':
      txModifier = shouldCommitTx
        ? tx.string.concat(__srcAttrName).done(__destAttr)
        : tx.string.concat(__srcAttrName);
      break;

    case 'concatAttrs':
      {
        let joinWithAttr = getValForParam(ruleParams, 'joinWithAttr', tx, true);
        let separator = getValForParam(ruleParams, 'separator');
        separator = !isUndefined(separator) && !isNull(separator) ? '$.' + separator : '$.';

        txModifier = shouldCommitTx
          ? tx.string.concat(__srcAttrName, separator, joinWithAttr).done(__destAttr)
          : tx.string.concat(__srcAttrName, separator, joinWithAttr);
      }
      break;

    case 'concatMultiple':
      {
        let elements = getValForParam(ruleParams, 'elements', tx, true, true);
        txModifier = shouldCommitTx
          ? tx.string.concat(...(elements || [])).done(__destAttr)
          : tx.string.concat(...(elements || []));
      }
      break;

    case 'join':
      {
        let delimiter = getValForParam(ruleParams, 'delimiter');
        delimiter = !isUndefined(delimiter) && !isNull(delimiter) ? delimiter : '';

        if (Array.isArray(ruleParams.__srcAttr)) {
          let joinSrcAttrNames = getValForParam(ruleParams, '__srcAttr', tx, true, true);

          const safeJoinSrcAttrNames = joinSrcAttrNames || [];

          txModifier = shouldCommitTx
            ? tx.string.join(delimiter, ...safeJoinSrcAttrNames).done(__destAttr)
            : tx.string.join(delimiter, ...safeJoinSrcAttrNames);
        } else {
          txModifier = shouldCommitTx
            ? tx.string.join(delimiter, __srcAttrName).done(__destAttr)
            : tx.string.join(delimiter, __srcAttrName);
        }
      }
      break;

    case 'split':
      {
        let delimiter = getValForParam(ruleParams, 'delimiter');
        txModifier = shouldCommitTx
          ? tx.string.split(delimiter, __srcAttrName).done(__destAttr)
          : tx.string.split(delimiter, __srcAttrName);
      }

      break;

    case 'toUpper':
      txModifier = shouldCommitTx
        ? tx.string.toUpper(__srcAttrName).done(__destAttr)
        : tx.string.toUpper(__srcAttrName);
      break;

    case 'toLower':
      txModifier = shouldCommitTx
        ? tx.string.toLower(__srcAttrName).done(__destAttr)
        : tx.string.toLower(__srcAttrName);
      break;

    case 'toString':
      txModifier = shouldCommitTx
        ? tx.string.toString(__srcAttrName).done(__destAttr)
        : tx.string.toString(__srcAttrName);
      break;

    case 'replaceAll':
      {
        let fromStr = getValForParam(ruleParams, 'fromStr');
        let toStr = getValForParam(ruleParams, 'toStr');
        toStr = !isUndefined(toStr) && !isNull(toStr) ? toStr : '';

        txModifier = shouldCommitTx
          ? tx.string.replaceAll(fromStr, toStr, __srcAttrName).done(__destAttr)
          : tx.string.replaceAll(fromStr, toStr, __srcAttrName);
      }
      break;

    case 'substring':
      {
        let fromPos = getValForParam(ruleParams, 'fromPos');
        let toPos = getValForParam(ruleParams, 'toPos');
        toPos = toPos ? parseInt(toPos) : null;
        if (isInteger(toPos)) {
          txModifier = shouldCommitTx
            ? tx.string.substring(__srcAttrName, fromPos, toPos).done(__destAttr)
            : tx.string.substring(__srcAttrName, fromPos, toPos);
        } else {
          txModifier = shouldCommitTx
            ? tx.string.substring(__srcAttrName, fromPos).done(__destAttr)
            : tx.string.substring(__srcAttrName, fromPos);
        }
      }
      break;

    case 'strlen':
      txModifier = shouldCommitTx
        ? tx.string.length(__srcAttrName).done(__destAttr)
        : tx.string.length(__srcAttrName);
      break;

    case 'trim':
      txModifier = shouldCommitTx
        ? tx.string.trim(__srcAttrName).done(__destAttr)
        : tx.string.trim(__srcAttrName);
      break;

    case 'toJsonString':
      txModifier = shouldCommitTx
        ? tx.string.toJsonString(__srcAttrName).done(__destAttr)
        : tx.string.toJsonString(__srcAttrName);
      break;

    case 'grok':
      {
        let grokPattern = getValForParam(ruleParams, 'grokPattern');
        txModifier = shouldCommitTx
          ? tx.string.grok(grokPattern, __srcAttrName).done(__destAttr)
          : tx.string.grok(grokPattern, __srcAttrName);
      }
      break;

    case 'regexFind':
      {
        let regexPattern = getValForParam(ruleParams, 'regexPattern');
        txModifier = shouldCommitTx
          ? tx.string.regexFind(regexPattern, __srcAttrName).done(__destAttr)
          : tx.string.regexFind(regexPattern, __srcAttrName);
      }
      break;

    case 'regexMatch':
      {
        let regexPattern = getValForParam(ruleParams, 'regexPattern');
        txModifier = shouldCommitTx
          ? tx.string.regexMatch(regexPattern, __srcAttrName).done(__destAttr)
          : tx.string.regexMatch(regexPattern, __srcAttrName);
      }
      break;

    //Array Operations
    case 'array_sort':
      txModifier = shouldCommitTx
        ? tx.array.sort(__srcAttrName).done(__destAttr)
        : tx.array.sort(__srcAttrName);
      break;

    case 'array_size':
      txModifier = shouldCommitTx
        ? tx.array.size(__srcAttrName).done(__destAttr)
        : tx.array.size(__srcAttrName);
      break;

    case 'firstElement':
      txModifier = shouldCommitTx
        ? tx.array.firstElement(__srcAttrName).done(__destAttr)
        : tx.array.firstElement(__srcAttrName);
      break;

    case 'lastElement':
      txModifier = shouldCommitTx
        ? tx.array.lastElement(__srcAttrName).done(__destAttr)
        : tx.array.lastElement(__srcAttrName);
      break;

    case 'elementAt':
      {
        let index = getValForParam(ruleParams, 'index');
        txModifier = shouldCommitTx
          ? tx.array.elementAt(index, __srcAttrName).done(__destAttr)
          : tx.array.elementAt(index, __srcAttrName);
      }

      break;

    //Location
    case 'country':
    case 'country_code':
    case 'city':
    case 'postal':
    case 'region':
    case 'continent':
    case 'latitude':
    case 'longitude':
    case 'dma':
    case 'timezone':
    case 'region_code':
      txModifier = shouldCommitTx
        ? tx.extract.geo(name, __srcAttrName).done(__destAttr)
        : tx.extract.geo(name, __srcAttrName);
      break;

    //Lookup
    case 'lookup':
      {
        let data_map = getValForParam(ruleParams, 'data_map');
        let dataMapId = _.isString(data_map) ? _.toNumber(data_map) : data_map;
        let subKey = getValForParam(ruleParams, 'subKey');
        if (subKey) {
          txModifier = shouldCommitTx
            ? tx.map.lookup(dataMapId, __srcAttrName, subKey).done(__destAttr)
            : tx.map.lookup(dataMapId, __srcAttrName, subKey);
        } else {
          txModifier = shouldCommitTx
            ? tx.map.lookup(dataMapId, __srcAttrName).done(__destAttr)
            : tx.map.lookup(dataMapId, __srcAttrName);
        }
      }

      break;

    case 'lookupList':
      {
        let data_map = getValForParam(ruleParams, 'data_map');
        let dataMapId = _.isString(data_map) ? _.toNumber(data_map) : data_map;
        let lookupKey = getValForParam(ruleParams, 'lookupKey', tx, true);
        txModifier = shouldCommitTx
          ? tx.map.lookupList(dataMapId, lookupKey).done(__destAttr)
          : tx.map.lookupList(dataMapId, lookupKey);
      }
      break;
    //Finance
    case 'USDConversionString':
      {
        //TODO: Need something better than a hardcoded ID
        let from_usd_to_cur = getValForParam(ruleParams, 'from_usd_to_cur');
        txModifier = shouldCommitTx
          ? tx.currency.convertString(101, from_usd_to_cur, __srcAttrName).done(__destAttr)
          : tx.currency.convertString(101, from_usd_to_cur, __srcAttrName);
      }

      break;

    //AI
    case 'oneHotEncoding':
      {
        let data_map = getValForParam(ruleParams, 'data_map');
        let dataMapId = _.isString(data_map) ? _.toNumber(data_map) : data_map;
        txModifier = shouldCommitTx
          ? tx.ai.oneHotEncoding(dataMapId, __srcAttrName).done(__destAttr)
          : tx.ai.oneHotEncoding(dataMapId, __srcAttrName);
      }
      break;

    //UserAgent
    case 'device_make':
    case 'device_type':
    case 'browser':
    case 'operating_system':
    case 'browser_version':
      txModifier = shouldCommitTx
        ? tx.extract.userAgent(name, __srcAttrName).done(__destAttr)
        : tx.extract.userAgent(name, __srcAttrName);
      break;

    //Date and Time
    case 'iso8601FromEpoch':
      txModifier = shouldCommitTx
        ? tx.date.iso8601FromEpoch(__srcAttrName).done(__destAttr)
        : tx.date.iso8601FromEpoch(__srcAttrName);
      break;

    case 'iso8601FromString':
      txModifier = shouldCommitTx
        ? tx.date.iso8601FromString(__srcAttrName).done(__destAttr)
        : tx.date.iso8601FromString(__srcAttrName);
      break;

    case 'convertTimeZone':
      {
        let toTz = getValForParam(ruleParams, 'toTz');
        let fromTz = getValForParam(ruleParams, 'fromTz');
        fromTz = !isUndefined(fromTz) ? fromTz : null;

        txModifier = shouldCommitTx
          ? tx.date.convertTimeZone(fromTz, toTz, __srcAttrName).done(__destAttr)
          : tx.date.convertTimeZone(fromTz, toTz, __srcAttrName);
      }
      break;

    case 'extractDate':
      {
        let componentPattern = getValForParam(ruleParams, 'componentPattern');
        txModifier = shouldCommitTx
          ? tx.date.extractDate(componentPattern, __srcAttrName).done(__destAttr)
          : tx.date.extractDate(componentPattern, __srcAttrName);
      }
      break;

    case 'timestampDifference': {
      let fromTs = getValForParam(ruleParams, 'fromTs');
      let diffUnit = getValForParam(ruleParams, 'diffUnit');
      txModifier = shouldCommitTx
        ? tx.date.timestampDifference(diffUnit, fromTs, __srcAttrName).done(__destAttr)
        : tx.date.timestampDifference(diffUnit, fromTs, __srcAttrName);
      break;
    }

    //Automotive
    case 'vin2country':
    case 'vin2year':
    case 'vin2manufacturer': {
      let ruleToApply = name;
      ruleToApply = _.replace(ruleToApply, 'vin2', '');
      txModifier = shouldCommitTx
        ? tx.extract.vin(ruleToApply, __srcAttrName).done(__destAttr)
        : tx.extract.vin(ruleToApply, __srcAttrName);
      break;
    }

    //Misc.
    case 'eqAttr':
      txModifier = shouldCommitTx
        ? tx.map.noop(__srcAttrName).done(__destAttr)
        : tx.map.noop(__srcAttrName);
      break;

    case 'constVal':
      {
        let val = getValForParam(ruleParams, 'val', tx, true);
        txModifier = shouldCommitTx ? tx.map.noop(val).done(__destAttr) : tx.map.noop(val);
      }

      break;

    case 'remove':
      txModifier = tx.removeAttribute(__srcAttrName);
      break;

    case 'hoist':
      txModifier = tx.hoistAttributes(__srcAttrName, '');
      break;

    case 'getNexsetData':
      {
        let dataSetId = getValForParam(ruleParams, 'dataSetId');
        let apiKey = getValForParam(ruleParams, 'apiKey');
        let selectors = getValForParam(ruleParams, 'selectors', tx, true, false, true);
        txModifier = shouldCommitTx
          ? tx.map.getNexsetData(dataSetId, apiKey, true, selectors).done(__destAttr)
          : tx.map.getNexsetData(dataSetId, apiKey, true, selectors);
      }
      break;

    case 'not?':
      txModifier = shouldCommitTx
        ? tx.conditional.not(__srcAttrName).done(__destAttr)
        : tx.conditional.not(__srcAttrName);
      break;

    case 'empty?':
      txModifier = shouldCommitTx
        ? tx.conditional.empty(__srcAttrName).done(__destAttr)
        : tx.conditional.empty(__srcAttrName);
      break;

    case 'and?':
      {
        let withAttr = getValForParam(ruleParams, 'withAttr', tx);

        txModifier = shouldCommitTx
          ? tx.conditional.and(__srcAttrName, withAttr).done(__destAttr)
          : tx.conditional.and(__srcAttrName, withAttr);
      }
      break;

    case 'or?':
      {
        let withAttr = getValForParam(ruleParams, 'withAttr', tx);

        txModifier = shouldCommitTx
          ? tx.conditional.or(__srcAttrName, withAttr).done(__destAttr)
          : tx.conditional.or(__srcAttrName, withAttr);
      }
      break;
    case 'equal?':
      {
        let withAttr = getValForParam(ruleParams, 'withAttr', tx, true);

        txModifier = shouldCommitTx
          ? tx.conditional.equal(__srcAttrName, withAttr).done(__destAttr)
          : tx.conditional.equal(__srcAttrName, withAttr);
      }
      break;

    case 'lessThan?':
      {
        let withAttr = getValForParam(ruleParams, 'withAttr', tx, true);

        txModifier = shouldCommitTx
          ? tx.conditional.lessThan(__srcAttrName, withAttr).done(__destAttr)
          : tx.conditional.lessThan(__srcAttrName, withAttr);
      }
      break;

    case 'greaterThan?':
      {
        let withAttr = getValForParam(ruleParams, 'withAttr', tx, true);

        txModifier = shouldCommitTx
          ? tx.conditional.greaterThan(__srcAttrName, withAttr).done(__destAttr)
          : tx.conditional.greaterThan(__srcAttrName, withAttr);
      }
      break;

    case 'ifElse':
      {
        let truthyAttr = getValForParam(ruleParams, 'truthyAttr', tx, true);
        let falsyAttr = getValForParam(ruleParams, 'falsyAttr', tx, true);

        txModifier = shouldCommitTx
          ? tx.conditional.ifElse(__srcAttrName, truthyAttr, falsyAttr).done(__destAttr)
          : tx.conditional.ifElse(__srcAttrName, truthyAttr, falsyAttr);
      }
      break;

    // case 'constNum':
    //   {
    // TODO: need changes in TransformComposer to handle a numerical literal
    //     let numVal = !_.isNaN(Number(newTransformRule.__params.val))
    //       ? Number(newTransformRule.__params.val)
    //       : null;
    //     console.log(numVal);
    //
    //     shouldCommitTx
    //      ? tx.map.noop(`$.${numVal}`).done(__destAttr)
    //      : tx.map.noop(`$.${numVal}`);
    //   }
    //   break;

    default:
      break;
  }

  if (name.startsWith('customAttrTx_')) {
    let transformId = getValForParam(ruleParams, 'attrTxId');
    let customTxId = _.isString(transformId) ? _.toNumber(transformId) : transformId;
    let txArgumentsArr = _.has(ruleParams, 'attrTxId.argValues')
      ? ruleParams.attrTxId.argValues
      : [];

    let funcParams = _.reduce(
      txArgumentsArr,
      (acc, thisArg, idx) => {
        const { type, name, value } = thisArg;
        let retVal = null;
        switch (type) {
          case TransformParameterTypes.ATTR_REF:
          case TransformParameterTypes.SYS_ATTR_REF:
            retVal = !_.isNil(name) ? name : null;
            break;

          case TransformParameterTypes.NUMBER_INP:
            retVal = !_.isNil(value) ? `$_${value}` : null;
            break;

          case TransformParameterTypes.STRING_INP:
          default:
            retVal = !_.isNil(value) ? `$.${value}` : null;
        }
        if (retVal) {
          acc.push(retVal);
        }

        return acc;
      },
      [],
    );

    txModifier = shouldCommitTx
      ? tx.map.customAttributeTransform(customTxId, funcParams).done(__destAttr)
      : tx.map.customAttributeTransform(customTxId, funcParams);
  }

  return shouldCommitTx ? tx : txModifier;
};

//Extact Transform Rules from txObject

function batchExtractTransformRules(txObject, metaDataSpec = {}, isCustom = false) {
  let extractedTx = {
    autoMappingEnabled: false,
    items: {},
    lastUpdated: Date.now(),
    orderByAttrId: [],
    customTx: null,
  };

  if (isCustom) {
    if (txObject === '[]') {
      extractedTx.autoMappingEnabled = true;
    } else {
      extractedTx.customTx = txObject;
    }
    return extractedTx;
  }

  let modifyRules = null,
    filtersRules = null,
    shareRules = null,
    defaultRules = null,
    removeRules = null;

  _.forEach(txObject, (txOp) => {
    switch (txOp.operation) {
      case 'nexla.modify':
        modifyRules = batchExtractModifyRules(txOp.spec, metaDataSpec);
        break;
      case 'nexla.filter':
        filtersRules = batchExtractFilterRules(txOp.spec);
        break;

      case 'shift':
        //TODO: temp 2.0.7 hack for tx reconstruct
        //shareRules = batchExtractShareRules(txOp.spec);
        shareRules = [];
        break;

      case 'default':
        defaultRules = batchExtractDefaultRules(txOp.spec);
        break;
      case 'remove':
        //TODO: temp 2.0.7 hack for tx reconstruct
        //removeRules = batchExtractShareRules(txOp.spec);
        removeRules = [];
        break;
      default:
        break;
    }
  });

  // let modifyAndFilterAttrs = keys(modifyRules);
  let modifyAndFilterAttrs = _.union(keys(modifyRules), keys(filtersRules), keys(defaultRules));
  let outputAttrs = _.union(shareRules, modifyAndFilterAttrs);
  // outputAttrs = _.pullAll(outputAttrs, removeRules);
  // let outputAttrs = modifyAndFilterAttrs;
  // console.log(outputAttrs);
  _.forEach(outputAttrs, (attr, idx) => {
    let share = {
      name: TransformParameterTypes.SHARE,
      __destAttr: attr,
      parameters: { __shouldShare: true },
    };
    let modify = _.has(modifyRules, attr) ? modifyRules[attr] : null;
    let filters = _.has(filtersRules, attr) ? filtersRules[attr] : null;
    let defaults = _.has(defaultRules, attr)
      ? defaultRules[attr]
      : {
          ...ATTR_DEFAULT_NONE,
          __destAttr: attr,
        };

    extractedTx.items[attr] = {
      modify,
      share,
      filters,
      defaults,
    };

    extractedTx.orderByAttrId = [...extractedTx.orderByAttrId, attr];
  });
  _.forEach(removeRules, (attr, idx) => {
    let share = {
      name: TransformParameterTypes.SHARE,
      __destAttr: attr,
      parameters: { __shouldShare: false },
    };
    if (_.has(extractedTx.items, attr)) {
      extractedTx.items[attr] = {
        ...extractedTx.items[attr],
        share: share,
      };
    } else {
      extractedTx.items[attr] = {
        share,
        modify: null,
        filters: null,
        defaults: null,
      };
      extractedTx.orderByAttrId = [...extractedTx.orderByAttrId, attr];
    }
  });

  // console.log('extracted tx');
  // console.log(JSONUtils.stringifyJson(extractedTx));
  return extractedTx;
}

////TODO: comment out to fix console warning
// function batchExtractShareRules(txShare) {
//   // "spec":{
//   //   "listPrice":"",
//   //   "salePrice":""
//   // }
//   let flatShare = flat(txShare, { safe: true });
//   let sharedAttrs = _.keys(flatShare);
//   return _.without(sharedAttrs, NEXLA_SHADOW_ATTR);
// }

function batchExtractFilterRules(txFilter) {
  //"spec":"$.[?($.['onAd'] == false \u0026\u0026 $.['onSale'] == false)]"
  //OR:
  //"spec":{
  // encoding:'base64',
  // filter: //
  //}
  let tx = TransformComposer.create();
  let decodedFilterSpec = tx.decodeFilterSpec(txFilter);
  // let parsedRule = parseFilterRule(decodedFilterSpec);
  let decomposedRule = FilterDecomposer.create(decodedFilterSpec);
  let decomposedObject = decomposedRule.getFilterObject();
  // console.log('extracting filter rule');
  // console.log(JSON.stringify(decomposedObject));
  // console.log(parsedRule);
  let extractedRules = constructFilterFromDecomposedRule(decomposedObject);
  // console.log(JSON.stringify(extractedRules));
  // let parsedRule = parseFilterRule(decodedFilterSpec);
  return extractedRules;
}

function batchExtractModifyRules(txModify, metaDataSpec = {}) {
  // "spec":{
  //   "listPrice-1":"=noop(@(1,'listPrice'))",
  //   "salePrice1":"=toInteger(@(1,'salePrice'))",
  //   "onAd":"=noop(@(1,'onAd'))",
  //   "onSale":"=noop(@(1,'onSale'))"
  // }
  let flatModify = flat(txModify, { safe: true });

  return _.mapValues(flatModify, (rule, attr) => {
    let decomposedRule = TransformDecomposer.create(rule);
    let decomposedObject = decomposedRule.getModifierObject();
    let constructedTx = constructTxFromParsedRule(decomposedObject, metaDataSpec);

    return { ...constructedTx, __destAttr: attr };
  });
}

function constructTxFromParsedRule(decomposedRule, metaDataSpec) {
  const { type, name, parameters } = decomposedRule;

  let paramsToProcess = parameters;
  if (TransformParameterTypes.FUNCTION !== type) {
    console.log('cannot construct tx for a non-function');
    return decomposedRule;
  } else if (parameters.length === 1 && parameters[0].type === TransformParameterTypes.ARRAY) {
    //TODO: kind of hack for a rule which only accepts an array.
    paramsToProcess = parameters[0].items;
  }

  let processedParams = reduce(
    paramsToProcess,
    (result, p, idx) => {
      if (!has(p, 'type')) {
        result[idx] = p;
        return result;
      }

      switch (p.type) {
        case TransformParameterTypes.FUNCTION:
          result[idx] = constructTxFromParsedRule(p, metaDataSpec);
          break;

        case TransformParameterTypes.ATTR_REF:
          {
            let attrName = has(p, 'parameters[1].value') ? p.parameters[1].value : null;
            result[idx] = {
              type: p.type,
              name: attrName,
            };
          }
          break;

        case TransformParameterTypes.SYS_ATTR_REF:
          {
            let attrName = has(p, 'parameters[0].value') ? p.parameters[0].value : null;
            let metaDataName = findKey(metaDataSpec, (md) => {
              return md.attributeName === attrName;
            });

            result[idx] = {
              type: p.type,
              name: metaDataName,
            };
          }
          break;

        default:
          result[idx] = p;
      }

      return result;
    },
    [],
  );

  let t = name;
  let p = {};

  //normally srcAttr is at param 0.
  let indexOfSrc = 0;
  let noSrcAttr = false;
  switch (name) {
    case 'stringHash':
      t = processedParams[0].value;
      indexOfSrc = 1;
      break;

    case 'abs':
      break;

    case 'add':
      p.add_with = processedParams[1];
      break;

    case 'diff':
      p.to_subtract = processedParams[1];
      break;
    //t='Subtract';

    case 'multiply':
      p.multiply_by = processedParams[1];
      break;

    case 'div':
      p.div_by = processedParams[1];
      break;

    case 'avg':
      break;

    case 'max':
      break;

    case 'min':
      break;

    case 'bucket':
      p.bucket_size = processedParams[0];
      indexOfSrc = 1;
      break;
    case 'toDouble':
      break;

    case 'toInteger':
      break;

    case 'toLong':
      break;

    case 'precision':
      p.scale = processedParams[0];
      indexOfSrc = 1;
      break;

    case 'not?':
    case 'empty?':
      break;

    case 'and?':
    case 'or?':
    case 'equal?':
    case 'lessThan?':
    case 'greaterThan?':
      p.withAttr = processedParams[1];
      break;

    case 'ifElse':
      p.truthyAttr = processedParams[1];
      p.falsyAttr = processedParams[2];
      break;

    case 'concat':
      if (processedParams.length > 1) {
        t = 'concatAttrs';
        indexOfSrc = 0;
        p.separator = processedParams[1];
        p.joinWithAttr = processedParams[2];
      } else {
        t = 'concatArr';
        indexOfSrc = 0;
      }
      break;

    case 'join':
      p.delimiter = processedParams[0];
      indexOfSrc = 1;
      break;

    case 'split':
      p.delimiter = processedParams[0];
      indexOfSrc = 1;
      break;

    case 'toUpper':
      break;

    case 'toLower':
      break;

    case 'toString':
      break;

    case 'replaceAll':
      p.fromStr = processedParams[0];
      p.toStr = processedParams[1];
      indexOfSrc = 2;
      break;

    case 'substring':
      p.fromPos = processedParams[0];
      if (processedParams.length > 2) {
        let toPos = processedParams[1];
        if (toPos === null || !toPos || (has(toPos, 'value') && toPos.value === 'null')) {
          toPos.value = '';
        }
        p.toPos = toPos;
        indexOfSrc = 2;
      } else {
        p.toPos = { ...PARAMS_PROTO_STR, value: '' };
        indexOfSrc = 1;
      }
      break;

    case 'strlen':
      break;

    case 'trim':
      break;

    case 'toJsonString':
      break;

    case 'grok':
      p.grokPattern = processedParams[0];
      indexOfSrc = 1;
      break;

    case 'regexFind':
    case 'regexMatch':
      {
        let regexParam = processedParams[0];
        if (!_.isNil(regexParam)) {
          regexParam.value = !_.isNil(regexParam.value) ? Base64.decode(regexParam.value) : '';
        }
        p.regexPattern = { ...regexParam };
        indexOfSrc = 1;
      }

      break;

    case 'sort':
      t = 'array_sort';
      break;

    case 'size':
      t = 'array_size';
      break;

    case 'firstElement':
      break;

    case 'lastElement':
      break;

    case 'elementAt':
      p.index = processedParams[0];
      indexOfSrc = 1;
      break;

    case 'extractDate':
      p.componentPattern = processedParams[0];
      indexOfSrc = 1;
      break;

    case 'GeoDetection':
      t = processedParams[0].value;
      indexOfSrc = 1;
      break;

    case 'toMapValue':
      t = 'lookup';
      p.data_map = processedParams[0];
      indexOfSrc = 1;
      break;

    case 'toMapValue2':
      t = 'lookup';
      p.data_map = processedParams[0];
      indexOfSrc = 1;
      if (processedParams.length > 2) {
        p.subKey = processedParams[2];
      }
      break;

    case 'getMap':
      t = 'lookupList';
      p.data_map = processedParams[0];
      noSrcAttr = true;
      p.lookupKey = processedParams[1];
      break;

    case 'oneHotEncoding':
      p.data_map = processedParams[0];
      indexOfSrc = 1;
      break;

    case 'USDConversionString':
      p.from_usd_to_cur = processedParams[1];
      indexOfSrc = 2;
      break;

    case 'UserAgentDetection':
      t = processedParams[0].value;
      indexOfSrc = 1;
      break;

    case 'iso8601FromString':
      break;

    case 'iso8601FromEpoch':
      break;

    case 'convertTimeZone':
      {
        let fromTz = processedParams[0];
        p.fromTz =
          fromTz.value === 'null' || fromTz.value === null
            ? { ...PARAMS_PROTO_STR, value: null }
            : fromTz;

        p.toTz = processedParams[1];
        indexOfSrc = 2;
      }
      break;

    case 'timestampDifference':
      p.diffUnit = processedParams[0];
      p.fromTs = processedParams[2];
      indexOfSrc = 1;
      break;

    case 'VINDetection':
      t = `vin2${processedParams[0].value}`;
      indexOfSrc = 1;
      break;

    case 'noop':
      {
        let equalsTo = processedParams[0];
        if (
          equalsTo.type !== TransformParameterTypes.ATTR_REF &&
          equalsTo.type !== TransformParameterTypes.SYS_ATTR_REF &&
          equalsTo.type !== TransformParameterTypes.FUNCTION
        ) {
          t = 'constVal';
          p.val = equalsTo;
          noSrcAttr = true;
        } else {
          t = 'eqAttr';
        }
      }

      break;

    case 'custom':
      {
        let funcArgs = processedParams[1];
        t = `customAttrTx_${processedParams[0].value}`;
        noSrcAttr = true;
        p.attrTxId = {
          type: TransformParameterTypes.NUMBER_INP,
          value: processedParams[0].value,
        };
        p.attrTxId.argValues =
          funcArgs && funcArgs.type === 'array' && _.has(funcArgs, 'items')
            ? _.reduce(
                funcArgs.items,
                (res, f) => {
                  const { type } = f;
                  let argV = null;
                  if (_.isNil(type)) {
                    return res;
                  }

                  switch (type) {
                    case TransformParameterTypes.ATTR_REF:
                      {
                        let attrName = has(f, 'parameters[1].value') ? f.parameters[1].value : null;
                        argV = attrName ? { type, name: attrName } : null;
                      }
                      break;

                    case TransformParameterTypes.SYS_ATTR_REF:
                      {
                        let attrName = has(f, 'parameters[0].value') ? f.parameters[0].value : null;
                        let metaDataName = attrName
                          ? findKey(metaDataSpec, (md) => {
                              return md.attributeName === attrName;
                            })
                          : null;

                        argV = metaDataName
                          ? {
                              type: f.type,
                              name: metaDataName,
                            }
                          : null;
                      }
                      break;

                    default:
                      argV = has(f, 'value') ? { type, value: f.value } : null;
                      break;
                  }

                  if (argV) {
                    res.push(argV);
                  }

                  return res;
                },
                [],
              )
            : [];
      }
      break;
    default:
      break;
  }

  p.__srcAttr =
    noSrcAttr === true ? { ...PARAMS_PROTO_ATTR, __ignoreSrc: true } : processedParams[indexOfSrc];
  //
  // tx.name = t;
  // tx.parameters = p;
  let constructedTx = {
    type,
    name: t,
    parameters: p,
  };

  return constructedTx;
}

function constructFilterFromDecomposedRule(decomposedRule) {
  if (
    !decomposedRule ||
    !_.has(decomposedRule, 'type') ||
    decomposedRule.type !== 'filters' ||
    !_.has(decomposedRule, 'parameters') ||
    _.isEmpty(decomposedRule.parameters)
  ) {
    return null;
  }
  let filtersByAttr = {};
  _.forEach(decomposedRule.parameters, (groupRule, idx) => {
    if (groupRule.type !== TransformParameterTypes.FILTER_GROUP) {
      //TODO: skip the groupJoinOp for now.
      // console.log('skipping group level rule');
      // console.log(groupRule);
      return;
    }
    // let groupParamCount = groupRule.parameters.length;
    _.forEach(groupRule.parameters, (filterRule, idx) => {
      //skip ruleJoinOp
      if (filterRule.type === TransformParameterTypes.FILTER_RULE_JOIN_OP) {
        // console.log('skipping filter rule join op');
        // console.log(filterRule);
        return;
      }

      //getattributename.
      let ruleOnAttr = _.join(filterRule.ruleAttr.attrParts, '.');

      let thisRule = {
        type: TransformParameterTypes.FILTER_RULE,
        ruleAttribute: ruleOnAttr,
      };
      let decomposedRuleOp = filterRule.ruleOp;

      // 4. convert ruleOp and ruleValue to UI format ruleOp and ruleValue.
      let thisRuleOp = FilterTypes.FILTER_NONE.value;
      let thisRuleVal = filterRule.ruleValue;

      switch (decomposedRuleOp) {
        case '==':
          thisRuleOp =
            filterRule.ruleValue.type === 'nullValue'
              ? FilterTypes.FILTER_IS_NULL.value
              : FilterTypes.FILTER_EQ.value;
          break;

        case '!=':
          thisRuleOp =
            filterRule.ruleValue.type === 'nullValue'
              ? FilterTypes.FILTER_NOT_NULL.value
              : FilterTypes.FILTER_NEQ.value;
          break;

        case '>':
          thisRuleOp = FilterTypes.FILTER_GT.value;
          break;

        case '<':
          thisRuleOp = FilterTypes.FILTER_LT.value;
          break;

        case '@':
          thisRuleOp = FilterTypes.FILTER_IS_PRESENT.value;
          thisRuleVal = { type: 'nullValue', value: null };
          break;

        case '!@':
          thisRuleOp = FilterTypes.FILTER_IS_NOT_PRESENT.value;
          thisRuleVal = { type: 'nullValue', value: null };
          break;

        case 'in':
          thisRuleOp = FilterTypes.FILTER_ARR_CONTAINS.value;
          break;

        case 'nin':
          thisRuleOp = FilterTypes.FILTER_ARR_DOESNT_CONTAIN.value;
          break;

        case '=~':
          if (filterRule.ruleValue.type === 'regexContains') {
            thisRuleOp = FilterTypes.FILTER_REGEX_CONTAINS.value;
          } else if (filterRule.ruleValue.type === 'regexBegin') {
            thisRuleOp = FilterTypes.FILTER_REGEX_BEGINS.value;
          } else if (filterRule.ruleValue.type === 'regexEnd') {
            thisRuleOp = FilterTypes.FILTER_REGEX_ENDS.value;
          } else {
            thisRuleOp = FilterTypes.FILTER_NONE.value;
          }
          thisRuleVal = { type: 'nullValue', value: null };
          if (
            !_.isNil(filterRule.ruleValue.parameters) &&
            !_.isEmpty(filterRule.ruleValue.parameters)
          ) {
            _.forEach(filterRule.ruleValue.parameters, (p, idx) => {
              if (p.type === 'caseParam') {
                thisRule.ruleOptions = { caseSensitive: p.value };
              } else {
                thisRuleVal = p;
              }
            });
            //in case DSL did not get caseParam node
            if (!_.has(thisRule, 'ruleOptions')) {
              thisRule.ruleOptions = { caseSensitive: true };
            }
          } else if (_.has(filterRule.ruleValue, 'value')) {
            thisRuleVal = filterRule.ruleValue;
          }
          break;

        default:
      }

      thisRule.ruleOp = thisRuleOp;
      thisRule.ruleVal = thisRuleVal;
      let attrFilterGroup = _.has(filtersByAttr, ruleOnAttr)
        ? filtersByAttr[ruleOnAttr]
        : { type: TransformParameterTypes.FILTER_GROUP, parameters: [] };

      let prevParam = !_.isEmpty(attrFilterGroup.parameters)
        ? _.last(attrFilterGroup.parameters)
        : null;
      if (prevParam && prevParam.type === TransformParameterTypes.FILTER_RULE) {
        //add rulejoin op;
        // console.log('add ruleJoinOp');
        // console.log(groupRule.parameters[idx - 1]);
        filtersByAttr[ruleOnAttr].parameters.push(groupRule.parameters[idx - 1]);
      }
      //add this filterop
      attrFilterGroup.parameters.push(thisRule);
      filtersByAttr[ruleOnAttr] = { ...attrFilterGroup };
    });
  });

  return filtersByAttr;
}

function batchExtractDefaultRules(txDefaults) {
  //first flatten the defaults object
  const flattenedDefaults = flat(txDefaults);
  //then iterate over each item and create a default object accordingly.
  const extractedDefaults = _.reduce(
    flattenedDefaults,
    (accDefaults, attrVal, attrName) => {
      if (attrName === NEXLA_SHADOW_ATTR) {
        return accDefaults;
      }

      let thisAttr = {
        ...ATTR_DEFAULT_NONE,
        __destAttr: attrName,
      };

      if (_.isNumber(attrVal)) {
        thisAttr.parameters = {
          defaultType: DefaultsTypes.DEFAULT_NUM,
          value: attrVal,
        };
      } else if (_.isString(attrVal)) {
        thisAttr.parameters = {
          defaultType: DefaultsTypes.DEFAULT_STR,
          value: attrVal,
        };
      } else if (_.isNull(attrVal)) {
        thisAttr.parameters = {
          defaultType: DefaultsTypes.DEFAULT_NULL,
          value: attrVal,
        };
      }
      //values can be null, number or string.
      //
      accDefaults[attrName] = thisAttr;
      return accDefaults;
    },
    {},
  );

  return extractedDefaults;
}

function getTransformObject(transforms) {
  const isCustomTx = !isEmpty(transforms.customTx);
  let tx = transforms.tx;
  let autoMappingEnabled = !!transforms.autoMappingEnabled;

  if (isCustomTx === false && autoMappingEnabled === false) {
    tx = removeNexlaShadowAttr(tx);
  }

  let transformObject = tx.getTransformObject();

  if (!isEmpty(transforms.customTx) || autoMappingEnabled) {
    transformObject.custom = true;
  }
  return transformObject;
}

export const convertCodeToTransform = (code, codeLanguage, options = {}) => {
  switch (codeLanguage) {
    case Constants.CodeLanguages.PYTHON:
    case Constants.CodeLanguages.PYTHON3:
    case Constants.CodeLanguages.JAVASCRIPT:
      return [
        {
          operation: 'nexla.custom',
          spec: {
            language: codeLanguage,
            encoding: 'base64',
            script: Base64.encode(code),
          },
        },
      ];

    case Constants.CodeLanguages.FLINK_SQL:
    case Constants.CodeLanguages.SPARK_SQL:
      return [
        {
          operation: 'nexla.custom',
          spec: {
            language: 'sql',
            code_type: codeLanguage,
            encoding: 'base64',
            script: Base64.encode(code),
            options,
          },
        },
      ];

    default:
      try {
        return JSON.parse(code);
      } catch (e) {
        throw new Error('Please enter valid JSON.');
      }
  }
};

export const convertTransformToCode = (transform) => {
  let code = null,
    codeLanguage = Constants.CodeLanguages.JOLT;

  if (!transform) return { code: '', codeLanguage };

  if (
    transform.length === 1 &&
    has(transform[0], 'operation') &&
    has(transform[0], 'spec.language') &&
    has(transform[0], 'spec.script') &&
    has(transform[0], 'spec.encoding')
  ) {
    //handle python & js code.
    const {
      operation,
      spec: { language, script, encoding },
    } = transform[0];

    if (
      operation === 'nexla.custom' &&
      script &&
      (language === Constants.CodeLanguages.PYTHON ||
        language === Constants.CodeLanguages.JAVASCRIPT ||
        language === Constants.CodeLanguages.PYTHON3)
    ) {
      code = encoding === 'base64' ? Base64.decode(script) : script;
      codeLanguage = language;
    }
  }

  if (!code) {
    code = JSONUtils.stringifyJson(transform);
    codeLanguage = Constants.CodeLanguages.JOLT;
  }

  return { code, codeLanguage };
};

export const decodeTranformCode = (code, encodingType = null) => {
  if (code && encodingType === 'base64' && _.isString(code)) {
    //NSTODO: looks like code containers in github have incorrect encordingType vs code settings.
    //should be fixed in API, but we should avoid this crash.
    return Base64.decode(code);
  }

  return code;
};

export const encodeTransformCode = (code, encodingType = 'base64') => {
  if (code && encodingType === 'base64') {
    return Base64.encode(code);
  }

  return code;
};

export const getTestCodeForCustomAttrTx = (
  attrTxId = null,
  argsTestData = [],
  outputAttrName = 'nexTestTxOut',
) => {
  let customTxId = _.isString(attrTxId) ? _.toNumber(attrTxId) : attrTxId;

  let tx = TransformComposer.create();

  let funcParams =
    !_.isNil(argsTestData) && !_.isEmpty(argsTestData)
      ? _.reduce(
          argsTestData,
          (acc, thisArg, idx) => {
            const { name, value } = thisArg;

            if (!_.has(thisArg, 'type')) {
              return acc;
            }

            let retVal = null;

            switch (thisArg.type) {
              case TransformParameterTypes.ATTR_REF:
              case TransformParameterTypes.SYS_ATTR_REF:
                retVal = !_.isNil(name) ? name : null;
                break;

              case TransformParameterTypes.NUMBER_INP:
                retVal = !_.isNil(value) ? `$_${value}` : null;
                break;

              case TransformParameterTypes.STRING_INP:
              default:
                retVal = !_.isNil(value) ? `$.${value}` : null;
            }
            if (retVal) {
              acc.push(retVal);
            }

            return acc;
          },
          [],
        )
      : [];

  tx.map.customAttributeTransform(customTxId, funcParams).done(outputAttrName);

  return tx.getTransformObject();
};

export const getCodeForReusableRecordTx = (txCode = '') => {
  let tx = TransformComposer.create();
  tx.setCustomTx(txCode);

  return tx.getTransformObject();
};

export const getUpgradedTxSpec = (dataSet, transforms) => {
  const dataSetTransform = dataSet.transform;
  const dataSetTransformId = dataSet && (dataSet.code_container_id || dataSet.transform_id);
  const hasCustomTransform = !!dataSetTransform?.custom;
  const autoMappingEnabled =
    hasCustomTransform &&
    isArray(dataSetTransform?.transforms) &&
    isEmpty(dataSetTransform?.transforms);
  const isTransformReusable = transforms[dataSetTransformId]?.reusable;
  const appliedTransformId = isTransformReusable ? dataSetTransformId : 0;
  const appliedOutputValidations = dataSet.custom_config?.outputValidations || [];
  let spec = [];

  if (appliedTransformId) {
    // reusable tx
    spec = [
      {
        type: RECIPE_GROUP_TRANSFORMS_CODE,
        data: {
          type: 'reusable_tx',
          transformId: appliedTransformId,
        },
      },
    ];
  } else if (!autoMappingEnabled) {
    if (
      hasCustomTransform &&
      dataSetTransform.transforms &&
      dataSetTransform.transforms[0].operation === 'nexla.custom'
    ) {
      // code transform
      const txElement = dataSetTransform.transforms[0].spec;
      spec = [
        {
          type: RECIPE_GROUP_TRANSFORMS_CODE,
          data: {
            type: 'custom_code',
            codeLanguage: txElement.language,
            code: decodeTranformCode(txElement.script, txElement.encoding),
            isAsync: dataSet?.runtime_config?.['node.tag'] === 'async',
          },
        },
      ];
    } else if (
      dataSetTransform &&
      isEmpty(dataSetTransform.custom_config) &&
      !isDetectedDataset(dataSet)
    ) {
      // show unrecognized transform as code
      spec = [
        {
          type: RECIPE_GROUP_TRANSFORMS_CODE,
          data: {
            type: 'custom_code',
            codeLanguage: Constants.CodeLanguages.JOLT,
            code: JSONUtils.stringifyJson(dataSetTransform.transforms),
          },
        },
      ];
    } else if (dataSetTransform) {
      // tx builder
      const decodedTransform = batchExtractTransformRules(dataSetTransform.transforms);
      const modify = [];
      const filters = [];
      const defaults = [];

      forEach(decodedTransform.items, (attrDef) => {
        modify.push(attrDef.modify);

        if (attrDef.filters) {
          filters.push(attrDef.filters);
        }

        const defaultType = attrDef.defaults.parameters?.defaultType;

        if (defaultType && defaultType !== 'DEFAULT_NONE') {
          defaults.push(attrDef.defaults);
        }
      });

      if (!isEmpty(modify)) {
        spec.push({
          type: RECIPE_GROUP_TRANSFORMS,
          data: modify,
        });
      }

      if (!isEmpty(defaults)) {
        spec.push({
          type: RECIPE_GROUP_DEFAULTS,
          data: defaults,
        });
      }

      if (!isEmpty(filters)) {
        spec.push({
          type: RECIPE_GROUP_FILTERS,
          data: filters,
        });
      }

      const annotationsData = [];

      forEach(dataSet.output_schema?.properties, (annotation, attr) => {
        if (!isEmpty(annotation?.description)) {
          annotationsData.push({
            attr,
            description: annotation.description,
          });
        }
      });

      if (!isEmpty(annotationsData)) {
        spec.push({
          type: RECIPE_GROUP_ANNOTATIONS,
          data: annotationsData,
        });
      }
    }
  } else if (autoMappingEnabled) {
    spec = [
      {
        type: RECIPE_GROUP_TRANSFORMS_REPLICATE,
        data: true,
      },
    ];
  }

  if (!isEmpty(appliedOutputValidations)) {
    spec.push({
      type: RECIPE_GROUP_VALIDATIONS,
      data: appliedOutputValidations,
    });
  }

  return spec;
};

/**
 * getTxFromRecipe_Original - Original single-block implementation from develop branch
 *
 * TEMPORARY: This is the exact implementation before PR #2889 multi-block support.
 * Used as a safety fallback when UI_ENABLE_MULTI_BLOCK_TRANSFORMS feature flag is OFF.
 *
 * REMOVE THIS FUNCTION after full rollout validation (few weeks)
 *
 * @param {Array} recipeGroups - Array of recipe group objects from the UI
 * @param {Object} transforms - Transform function definitions (for DSL parsing)
 * @param {boolean} isLegacyMode - Legacy parameter (for backwards compatibility)
 * @returns {Object} { txObject, saObject, reusableTransformId, outputValidations, parsingErrors }
 */
const getTxFromRecipe_Original = (recipeGroups, transforms = null, isLegacyMode = false) => {
  // tx builder
  let txBuilderRelatedGroups = filter(recipeGroups, ({ type }) =>
    TX_BUILDER_RECIPE_GROUPS.includes(type),
  );

  // in legacy mode, remove the code from each recipe
  if (isLegacyMode) {
    txBuilderRelatedGroups = map(txBuilderRelatedGroups, (recipeGroup) =>
      recipeGroup.type === RECIPE_GROUP_TRANSFORMS
        ? {
            ...recipeGroup,
            data: map(recipeGroup.data, (recipe) => ({
              ...recipe,
              code: undefined,
            })),
          }
        : recipeGroup,
    );
  }

  txBuilderRelatedGroups = completeTxBuilderRecipeGroups(txBuilderRelatedGroups, transforms);

  const parsingErrors = {};
  const tx = reduce(
    txBuilderRelatedGroups,
    (acc, { type, data }) => {
      let modify = null;
      let filters = null;
      let defaults = null;

      return reduce(
        data,
        (groupAcc, recipe) => {
          if (recipe.disabled) {
            return groupAcc;
          }

          switch (type) {
            case RECIPE_GROUP_TRANSFORMS:
              if (recipe?.code?.trim?.()) {
                try {
                  modify = TransformDSLParser.parse(recipe.code, transforms);

                  if (modify.parentPath && modify.__destAttr) {
                    modify.__destAttr = `${modify.parentPath}.${modify.__destAttr}`;
                    delete modify.parentPath;
                  }
                } catch (e) {
                  parsingErrors[recipe.code] = e.message;
                }
              }
              break;

            case RECIPE_GROUP_FILTERS:
              filters = recipe;
              break;

            case RECIPE_GROUP_DEFAULTS:
              defaults = recipe;
              break;

            default:
          }

          try {
            return composeTxForRule(acc, null, modify, filters, defaults);
          } catch (e) {
            console.log('error in composeTxForRule()', { modify, filters, defaults }, e);
            return acc;
          }
        },
        acc,
      );
    },
    initTx(),
  );

  // replicate
  const replicateRecipeGroup = find(recipeGroups, {
    type: RECIPE_GROUP_TRANSFORMS_REPLICATE,
    data: true,
  });

  if (replicateRecipeGroup) {
    setShareAllAttributes(true, tx);
  }

  const txObject = getTransformObject({ tx });

  // code transform
  const codeRecipeGroup = find(
    recipeGroups,
    ({ type }) =>
      type === RECIPE_GROUP_TRANSFORMS_CODE || type === RECIPE_GROUP_TRANSFORMS_AGGREGATE,
  );
  let reusableTransformId = null;

  if (codeRecipeGroup) {
    const {
      data: { type, code, codeLanguage, transformId, options },
    } = codeRecipeGroup;

    if (type === 'custom_code') {
      txObject.transforms = convertCodeToTransform(code, codeLanguage, options);
    } else {
      reusableTransformId = transformId;
    }
  }

  // validations
  const validationGroups = map(filter(recipeGroups, { type: RECIPE_GROUP_VALIDATIONS }), 'data');
  const outputValidations = [].concat.apply([], validationGroups);

  // annotations
  const annotationGroups = map(filter(recipeGroups, { type: RECIPE_GROUP_ANNOTATIONS }), 'data');
  const annotationRules = [].concat.apply([], annotationGroups);
  const annotationOrder = [];
  const annotationItems = {};

  forEach(annotationRules, ({ attr, description }) => {
    annotationOrder.push(attr);
    annotationItems[attr] = {
      __name: attr,
      __description: description,
    };
  });

  const sa = SchemaAnnotationUtils.batchComposeAnnotations(
    {
      items: annotationItems,
      orderByAttrId: annotationOrder,
    },
    SchemaAnnotater.createSchemaAnnotater(),
  );
  const saObject = sa.getObject();

  return {
    txObject,
    saObject,
    reusableTransformId,
    outputValidations,
    parsingErrors,
  };
};

/**
 * getTxFromRecipe - Converts UI recipe groups into JOLT transformation specification
 *
 * This function is the main orchestrator for generating JOLT (JSON to JSON transformation)
 * from the UI's recipe configuration. It handles two distinct operating modes:
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * MODE 1: LEGACY MODE
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * - Triggered when: Single TRANSFORMS block + optional FILTERS/DEFAULTS (no CODE blocks)
 * - Behavior: All operations stay in ONE block, NO nexla.end_block is inserted
 * - Output structure: shift → default → nexla.modify → remove → [nexla.filter]
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * MODE 2: NEW MULTI-BLOCK MODE
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * - Triggered when: Multiple TRANSFORMS blocks OR any CODE/AGGREGATE blocks
 * - Behavior: Each TRANSFORMS, CODE, FILTERS, DEFAULTS is its own block
 * - nexla.end_block is inserted between blocks
 *
 * Within New Mode, there are two sub-scenarios:
 *
 * A) BUILDER-FIRST (first block is TRANSFORMS/FILTERS/DEFAULTS):
 *    - First builder block gets FULL scaffolding: shift → default → modify → remove
 *    - Subsequent builder blocks only output: nexla.modify (or nexla.filter/default)
 *    - Scaffolding (shift, default, remove) is tied to the FIRST builder block
 *
 * B) CODE-FIRST (first block is CODE or AGGREGATE):
 *    - NO scaffolding for ANY builder blocks
 *    - Builder blocks only output their operations: nexla.modify, nexla.filter, default
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * SCAFFOLDING EXPLAINED
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * "Scaffolding" refers to the structural JOLT operations that wrap transform logic:
 *
 * - shift: Moves source attributes into the output structure
 *          Includes shadow attribute: #__#__NEX_ATTR__#__# (used for internal tracking)
 *
 * - default: Sets default values for attributes
 *            Includes: { "#__#__NEX_ATTR__#__#": 1 } for scaffolding
 *            May also include user-defined defaults: { "attr": "value" }
 *
 * - remove: Removes temporary/source attributes from output
 *           Includes: { "#__#__NEX_ATTR__#__#": "", "sourceAttr": "" }
 *
 * When code runs first, the schema is "reset" by the code block, so scaffolding
 * from builder blocks would be redundant/incorrect. Hence, code-first = no scaffolding.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 *
 * @param {Array} recipeGroups - Array of recipe group objects from the UI
 *        Each group has: { type: RECIPE_GROUP_*, data: [...] }
 * @param {Object} transforms - Transform function definitions (for DSL parsing)
 * @param {boolean} isLegacyMode - Legacy parameter (for backwards compatibility)
 * @param {boolean} enableMultiBlock - Feature flag to enable multi-block mode (default: false)
 *        When false, always uses legacy single-block mode for backward compatibility
 * @returns {Object} { txObject, saObject, reusableTransformId, outputValidations, parsingErrors }
 */
export const getTxFromRecipe = (
  recipeGroups,
  transforms = null,
  isLegacyMode = false,
  enableMultiBlock = false,
) => {
  // ═════════════════════════════════════════════════════════════════════════════
  // EARLY EXIT: Feature Flag Disabled - Use Original Implementation
  // ═════════════════════════════════════════════════════════════════════════════
  // TEMPORARY: Remove this check after full rollout validation (few weeks)
  // ═════════════════════════════════════════════════════════════════════════════
  if (!enableMultiBlock) {
    return getTxFromRecipe_Original(recipeGroups, transforms, isLegacyMode);
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // NEW MULTI-BLOCK IMPLEMENTATION (PR #2889)
  // ═════════════════════════════════════════════════════════════════════════════
  // Code below this point only executes when enableMultiBlock === true
  // This is the new logic that supports multiple TRANSFORMS/CODE blocks
  // ═════════════════════════════════════════════════════════════════════════════

  const parsingErrors = {};
  const allTransforms = []; // Accumulates all JOLT operations
  const dataMapsSet = new Set(); // Collects data_map references
  const dataSetsSet = new Set(); // Collects data_set references
  let isFirstBlock = true; // Tracks if we're processing the first block (for end_block insertion)
  let currentTxBuilderBlock = []; // Accumulates recipe groups for current tx_builder block
  let blockCount = 0; // Counts total blocks processed
  let reusableTransformId = null; // Stores reusable transform ID if present
  let firstBlockIsCode = false; // True if first block is CODE/AGGREGATE (affects scaffolding)
  let firstBuilderBlockProcessed = false; // True after first builder block is processed

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 1: Determine if the first block is a code block
  // This is critical for scaffolding decisions:
  // - If code is first: NO scaffolding for any builder blocks
  // - If builder is first: scaffolding goes on the FIRST builder block only
  //
  // NOTE: Disabled blocks are skipped when determining the first block
  // ─────────────────────────────────────────────────────────────────────────────
  for (let i = 0; i < recipeGroups.length; i++) {
    const { type, disabled } = recipeGroups[i];

    // Skip disabled blocks
    if (disabled) {
      continue;
    }

    const isCodeBlock =
      type === RECIPE_GROUP_TRANSFORMS_CODE || type === RECIPE_GROUP_TRANSFORMS_AGGREGATE;
    const isTxBuilder = TX_BUILDER_RECIPE_GROUPS.includes(type);

    if (isCodeBlock) {
      firstBlockIsCode = true;
      break;
    } else if (isTxBuilder) {
      firstBlockIsCode = false;
      break;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 2: Detect Legacy Mode vs New Mode
  //
  // Legacy Mode: The original behavior where we only supported:
  //   - One TRANSFORMS block
  //   - Optional FILTERS block
  //   - Optional DEFAULTS block
  //   All combined into a single JOLT block with no nexla.end_block
  //
  // New Mode: Multi-block support where each group type is its own block
  //   with nexla.end_block separating them
  // ─────────────────────────────────────────────────────────────────────────────
  let isLegacyBlockMode = false;
  let transformsCount = 0;
  let hasCodeBlock = false;

  for (let i = 0; i < recipeGroups.length; i++) {
    const { type, disabled } = recipeGroups[i];

    // Skip disabled blocks when determining mode
    if (disabled) {
      continue;
    }

    if (type === RECIPE_GROUP_TRANSFORMS) {
      transformsCount++;
    }
    if (type === RECIPE_GROUP_TRANSFORMS_CODE || type === RECIPE_GROUP_TRANSFORMS_AGGREGATE) {
      hasCodeBlock = true;
    }
  }

  // Legacy mode criteria: exactly ONE TRANSFORMS group AND no code blocks
  // This preserves backward compatibility with existing single-block recipes
  isLegacyBlockMode = transformsCount === 1 && !hasCodeBlock;

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 3: Define helper function to process tx_builder blocks
  //
  // This function takes a collection of recipe groups (TRANSFORMS, FILTERS, DEFAULTS)
  // and generates the corresponding JOLT operations.
  //
  // Key parameter: skipScaffolding
  // - When TRUE: Only output nexla.modify, nexla.filter, and user defaults
  //              (skip shift, scaffolding default, remove)
  // - When FALSE: Output full scaffolding + operations
  //               (shift, default, modify, remove, filter)
  //
  // skipScaffolding is TRUE when:
  //   1. Code block came first (firstBlockIsCode = true), OR
  //   2. This is NOT the first builder block (firstBuilderBlockProcessed = true)
  // ─────────────────────────────────────────────────────────────────────────────
  const processTxBuilderBlock = (blockGroups, skipScaffolding) => {
    // Handle legacy mode transformation (strips code from recipes)
    let processedBlock = isLegacyMode
      ? map(blockGroups, (group) =>
          group.type === RECIPE_GROUP_TRANSFORMS
            ? {
                ...group,
                data: map(group.data, (recipe) => ({
                  ...recipe,
                  code: undefined,
                })),
              }
            : group,
        )
      : blockGroups;

    // Complete the recipe groups with any additional processing
    processedBlock = completeTxBuilderRecipeGroups(processedBlock, transforms);

    // ─────────────────────────────────────────────────────────────────────────
    // Initialize the TransformComposer and process each recipe
    //
    // initTx(skipScaffolding) creates a new TransformComposer instance
    // The skipScaffolding flag (passed as hasCustomTransform) controls whether
    // the shadow attribute (#__#__NEX_ATTR__#__#) is added to the shift/default
    // ─────────────────────────────────────────────────────────────────────────
    const tx = reduce(
      processedBlock,
      (acc, { type, data }) => {
        let modify = null;
        let filters = null;
        let defaults = null;

        return reduce(
          data,
          (groupAcc, recipe) => {
            // Skip disabled recipes
            if (recipe.disabled) {
              return groupAcc;
            }

            // Parse recipe based on its type
            switch (type) {
              case RECIPE_GROUP_TRANSFORMS:
                // Parse the DSL code into a transform rule object
                // DSL format: "destAttr = FunctionName ( $(sourceAttr) )"
                if (recipe?.code?.trim?.()) {
                  try {
                    modify = TransformDSLParser.parse(recipe.code, transforms);

                    // Handle parent path for nested attributes
                    if (modify.parentPath && modify.__destAttr) {
                      modify.__destAttr = `${modify.parentPath}.${modify.__destAttr}`;
                      delete modify.parentPath;
                    }
                  } catch (e) {
                    parsingErrors[recipe.code] = e.message;
                  }
                }
                break;

              case RECIPE_GROUP_FILTERS:
                // Filter rules are passed directly
                filters = recipe;
                break;

              case RECIPE_GROUP_DEFAULTS:
                // Default rules are passed directly
                defaults = recipe;
                break;

              default:
            }

            // Apply the rule to the TransformComposer
            try {
              return composeTxForRule(acc, null, modify, filters, defaults);
            } catch (e) {
              console.log('error in composeTxForRule()', { modify, filters, defaults }, e);
              return acc;
            }
          },
          acc,
        );
      },
      initTx(skipScaffolding), // Initialize with skipScaffolding flag
    );

    // Handle replicate (share all attributes) mode
    const replicateInBlock = find(processedBlock, {
      type: RECIPE_GROUP_TRANSFORMS_REPLICATE,
      data: true,
    });

    if (replicateInBlock) {
      setShareAllAttributes(true, tx);
    }

    // Build the final JOLT transformation object
    const txObject = getTransformObject({ tx });

    // ─────────────────────────────────────────────────────────────────────────
    // CRITICAL: Filter operations based on skipScaffolding flag
    //
    // When skipScaffolding is TRUE (code-first or subsequent builder blocks):
    //   - SKIP: shift, remove, scaffolding default (has #__#__NEX_ATTR__#__#)
    //   - KEEP: nexla.modify, nexla.filter, user-defined default
    //
    // When skipScaffolding is FALSE (first builder block in builder-first):
    //   - KEEP ALL: shift, default, nexla.modify, remove, nexla.filter
    //
    // This filtering is what enables the "scaffolding tied to first block" behavior
    // ─────────────────────────────────────────────────────────────────────────
    if (txObject.transforms && txObject.transforms.length > 0) {
      if (skipScaffolding) {
        // Filter out scaffolding operations, keep only business logic
        forEach(txObject.transforms, (transform) => {
          // Distinguish between scaffolding default and user-defined default
          // Scaffolding default has the shadow attribute key in its spec
          // User-defined default only has user attribute keys
          const isScaffoldingDefault =
            transform.operation === 'default' && transform.spec?.[NEXLA_SHADOW_ATTR] !== undefined;

          // Keep the operation if it's NOT scaffolding
          if (
            transform.operation !== 'shift' &&
            !isScaffoldingDefault &&
            transform.operation !== 'remove'
          ) {
            allTransforms.push(transform);
          }
        });
      } else {
        // First builder block in builder-first mode: include ALL operations
        // This gives us: shift → default → nexla.modify → remove
        allTransforms.push(...txObject.transforms);
      }
    }

    // Collect data_maps and data_sets for the final output
    if (txObject.data_maps) {
      txObject.data_maps.forEach((id) => dataMapsSet.add(id));
    }
    if (txObject.data_sets) {
      txObject.data_sets.forEach((id) => dataSetsSet.add(id));
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 4: Main Processing Loop
  //
  // Iterates through recipe groups and builds the JOLT transformation.
  // The logic handles:
  //   - When to start a new block (and insert nexla.end_block)
  //   - When to accumulate groups into a single block (legacy mode)
  //   - When to process code blocks directly vs builder blocks
  //
  // Block Types:
  //   - CODE blocks (TRANSFORMS_CODE, TRANSFORMS_AGGREGATE): Processed immediately
  //   - Builder blocks (TRANSFORMS, FILTERS, DEFAULTS): Accumulated, then processed
  // ─────────────────────────────────────────────────────────────────────────────
  forEach(recipeGroups, (recipeGroup, index) => {
    const { type, disabled } = recipeGroup;

    // ─────────────────────────────────────────────────────────────────────────
    // Skip disabled blocks entirely - they don't contribute to JOLT output
    // ─────────────────────────────────────────────────────────────────────────
    if (disabled) {
      return; // Skip this iteration
    }

    // Determine previous group characteristics for block continuation logic
    const prevGroup = index > 0 ? recipeGroups[index - 1] : null;
    const prevWasBlock =
      prevGroup &&
      (TX_BUILDER_RECIPE_GROUPS.includes(prevGroup.type) ||
        prevGroup.type === RECIPE_GROUP_TRANSFORMS_CODE ||
        prevGroup.type === RECIPE_GROUP_TRANSFORMS_AGGREGATE);

    // Classify current group
    const isTxBuilder = TX_BUILDER_RECIPE_GROUPS.includes(type); // TRANSFORMS, FILTERS, DEFAULTS
    const isCodeBlock =
      type === RECIPE_GROUP_TRANSFORMS_CODE || type === RECIPE_GROUP_TRANSFORMS_AGGREGATE;
    const isBlock = isTxBuilder || isCodeBlock;

    // ─────────────────────────────────────────────────────────────────────────
    // Block Start Decision
    //
    // Legacy Mode: Only start a new block on the FIRST group
    //   - TRANSFORMS + FILTERS + DEFAULTS all stay in ONE block
    //   - No nexla.end_block is ever inserted
    //
    // New Mode: Each block-type group starts a NEW block
    //   - TRANSFORMS #1 → end_block → TRANSFORMS #2 → end_block → FILTERS
    //   - Each transition inserts nexla.end_block
    // ─────────────────────────────────────────────────────────────────────────
    const shouldStartNewBlock = isLegacyBlockMode
      ? isBlock && !prevWasBlock // Legacy: only start block on first group
      : isBlock; // New mode: each group is its own block

    if (shouldStartNewBlock) {
      // ─────────────────────────────────────────────────────────────────────
      // Finish any pending tx_builder block before starting new one
      // ─────────────────────────────────────────────────────────────────────
      if (currentTxBuilderBlock.length > 0) {
        // Determine if scaffolding should be skipped for this block
        // Skip if: code came first OR we've already processed a builder block
        const skipScaffolding = firstBlockIsCode || firstBuilderBlockProcessed;
        processTxBuilderBlock(currentTxBuilderBlock, skipScaffolding);

        // Mark that we've processed a builder block (affects future scaffolding)
        if (!firstBlockIsCode) {
          firstBuilderBlockProcessed = true;
        }
        currentTxBuilderBlock = [];
        blockCount++;
      }

      // ─────────────────────────────────────────────────────────────────────
      // Insert nexla.end_block between blocks (not before first block)
      // This separator tells the backend where one processing block ends
      // and another begins
      // ─────────────────────────────────────────────────────────────────────
      if (!isFirstBlock) {
        allTransforms.push({ operation: 'nexla.end_block' });
      }
      isFirstBlock = false;

      // ─────────────────────────────────────────────────────────────────────
      // Handle the current group based on its type
      // ─────────────────────────────────────────────────────────────────────
      if (isCodeBlock) {
        // CODE blocks are processed immediately (not accumulated)
        const {
          data: { type: codeType, code, codeLanguage, transformId, options },
        } = recipeGroup;

        if (codeType === 'custom_code') {
          // Convert Python/JS code to nexla.custom operation
          const codeTransforms = convertCodeToTransform(code, codeLanguage, options);
          allTransforms.push(...codeTransforms);
        } else if (codeType === 'reusable_tx') {
          // Reusable transform reference - backend will apply it
          if (!reusableTransformId) {
            reusableTransformId = transformId;
          }
        }
        blockCount++;
      } else {
        // Builder blocks (TRANSFORMS, FILTERS, DEFAULTS) are accumulated
        // They'll be processed when the next block starts or at the end
        currentTxBuilderBlock.push(recipeGroup);
      }
    } else if (isLegacyBlockMode && isTxBuilder && prevWasBlock) {
      // ─────────────────────────────────────────────────────────────────────
      // LEGACY MODE: Continue accumulating into current block
      // In legacy mode, FILTERS and DEFAULTS following a TRANSFORMS
      // are part of the same block (no nexla.end_block between them)
      // ─────────────────────────────────────────────────────────────────────
      currentTxBuilderBlock.push(recipeGroup);
    } else {
      // ─────────────────────────────────────────────────────────────────────
      // Non-block group encountered - finish any pending builder block
      // This handles edge cases like VALIDATIONS, ANNOTATIONS, etc.
      // ─────────────────────────────────────────────────────────────────────
      if (currentTxBuilderBlock.length > 0) {
        const skipScaffolding = firstBlockIsCode || firstBuilderBlockProcessed;
        processTxBuilderBlock(currentTxBuilderBlock, skipScaffolding);
        if (!firstBlockIsCode) {
          firstBuilderBlockProcessed = true;
        }
        currentTxBuilderBlock = [];
        blockCount++;
      }
    }
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 5: Process any remaining tx_builder block
  // This handles the case where the last group is a builder type
  // ─────────────────────────────────────────────────────────────────────────────
  if (currentTxBuilderBlock.length > 0) {
    const skipScaffolding = firstBlockIsCode || firstBuilderBlockProcessed;
    processTxBuilderBlock(currentTxBuilderBlock, skipScaffolding);
  }

  // Build final transform object
  const txObject = {
    version: 1,
    transforms: allTransforms,
    data_maps: Array.from(dataMapsSet),
  };

  if (dataSetsSet.size > 0) {
    txObject.data_sets = Array.from(dataSetsSet);
  }

  // Note: Reusable transforms are already handled in the sequential loop above
  // reusableTransformId is set when processing code blocks with type 'reusable_tx'

  // validations
  const validationGroups = map(filter(recipeGroups, { type: RECIPE_GROUP_VALIDATIONS }), 'data');
  const outputValidations = [].concat.apply([], validationGroups);

  // annotations
  const annotationGroups = map(filter(recipeGroups, { type: RECIPE_GROUP_ANNOTATIONS }), 'data');
  const annotationRules = [].concat.apply([], annotationGroups);
  const annotationOrder = [];
  const annotationItems = {};

  forEach(annotationRules, ({ attr, description }) => {
    annotationOrder.push(attr);
    annotationItems[attr] = {
      __name: attr,
      __description: description,
    };
  });

  const sa = SchemaAnnotationUtils.batchComposeAnnotations(
    {
      items: annotationItems,
      orderByAttrId: annotationOrder,
    },
    SchemaAnnotater.createSchemaAnnotater(),
  );
  const saObject = sa.getObject();

  return {
    txObject,
    saObject,
    reusableTransformId,
    outputValidations,
    parsingErrors,
  };
};

export const getSplitterTransform = (
  mode,
  expressions,
  attr,
  values,
  fallback,
  splitterDataSet = null,
  isTest = false,
) => {
  const escapeQuotes = (str) => str?.replaceAll('"', '\\"');
  let splitStatements;

  if (mode === 'expression') {
    splitStatements = expressions;
  } else {
    const orFunc = filterFunctions.find(({ value }) => value === 'or?');
    const eqFunc = filterFunctions.find(({ value }) => value === FilterTypesKeys.FILTER_EQ);
    splitStatements = values.map(({ valueList, dataSetId }) => {
      const valueExpressions = valueList.map((value) => {
        let valueStr;
        if (typeof value === 'boolean' || typeof value === 'number') {
          valueStr = value;
        } else {
          valueStr = `"${escapeQuotes(value)}"`;
        }

        return `${eqFunc.label}( \$(${attr}), ${valueStr} )`;
      });

      return {
        code:
          valueExpressions.length > 1
            ? `${orFunc.label}( ${valueExpressions.join(', ')} )`
            : valueExpressions[0],
        dataSetId,
      };
    });
  }

  const ast = [];
  const splitRules = splitStatements.map(({ code, name, discard, dataSetId }, index) => {
    const { filterExpression, filterAST } = FilterDSLParser.parse(code);
    ast.push(filterAST);

    return {
      filter: {
        type: 'jsonpath',
        encoding: 'base64',
        expression: filterExpression,
      },
      destination: discard
        ? { type: 'discard' }
        : dataSetId
          ? {
              type: 'nexset',
              id: dataSetId,
              name,
            }
          : {
              type: 'nexset',
              name,
              ...(isTest ? { id: index + 1 } : {}),
            },
    };
  });
  const fallbackDataSetId = isTest
    ? 100
    : splitterDataSet?.transform?.transforms?.[0]?.spec?.fallback_destination?.id;

  return {
    ast,
    transform: {
      version: 1,
      transforms: [
        {
          operation: 'nexla.splitter',
          spec: {
            rules: splitRules,
            fallback_destination:
              fallback === 'fallback_discard'
                ? { type: 'discard' }
                : fallbackDataSetId
                  ? {
                      type: 'nexset',
                      id: fallbackDataSetId,
                    }
                  : {
                      type: 'nexset',
                      name: 'Fallback',
                    },
          },
        },
      ],
    },
  };
};

var TransformsUtils = {
  getTransformForRule,
  initTx,
  setShareAllAttributes,
  addNexlaShadowAttr,
  removeNexlaShadowAttr,
  setFlattenFirst,
  validateTxRule,
  validateFilterRule,
  composeTxForRule,
  batchComposeTxForRules,
  batchExtractTransformRules,
  setTransformFeatures,
  getTransformSpecForFlatToNested,
  getTransformObject,
  convertCodeToTransform,
  convertTransformToCode,
  decodeTranformCode,
  encodeTransformCode,
  getTestCodeForCustomAttrTx,
  getCodeForReusableRecordTx,
  getUpgradedTxSpec,
  getTxFromRecipe,
  getSplitterTransform,
};

export default TransformsUtils;
